import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useSnackbar } from 'notistack';
import CustomTable from '../../components/CustomTable';
import SearchBar from '../../components/search.component';
import ClientDetail from './client-details';
import ClientForm from './client-form';
import ClientPanel from './ClientPanel';
import DeleteConfirmDialog from '../../components/deleteConfirmation.component';
import apiRequest from '../../services/apiClient';
import { formatDate } from '../../utils/utils';
import { invalidateCache } from '../../components/serverAutocomplete.component';

const Clients = ({ setTitleProps }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [clients, setClients] = useState({ items: [], totalCount: 0 });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [sortModel, setSortModel] = useState([{ field: 'name', sort: 'asc' }]);
  const [filters, setFilters] = useState({ searchQuery: '' });
  const [selectedItem, setSelectedItem] = useState();
  const [showForm, setShowForm] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState();
  const isExecutingRef = useRef(false);

  const loadClients = () => {
    if (isExecutingRef.current) return;
    isExecutingRef.current = true;

    try {
      setLoading(true);
      const order = sortModel && sortModel.length > 0
        ? `&$orderby=${sortModel[0].field} ${sortModel[0].sort}`
        : "";

      const searchQueryFilter = filters?.searchQuery && filters.searchQuery.length > 0 && filters.searchQuery.trim().length > 0
        ? `contains(name,'${filters.searchQuery}') or contains(identityNumber,'${filters.searchQuery}')`
        : "";

      let filter = searchQueryFilter;
      
      if (filter && filter.length > 0)
        filter = "&$filter=" + filter;

      apiRequest(`client/paged?$select=id,created,name,identityNumber&$top=${pageSize}&$skip=${page*pageSize}${order}${filter}`)
        .then((response) => {
          setClients({
            items: response.data.items || response.data,
            totalCount: response.data.totalCount || response.totalCount || 0
          });
          setLoading(false);
          isExecutingRef.current = false;
        })
        .catch((error) => {
          console.error('Error loading clients:', error);
          setLoading(false);
          isExecutingRef.current = false;
        });
    } catch (error) {
      console.error('Error in loadClients:', error);
      setLoading(false);
      isExecutingRef.current = false;
    }
  };

  useEffect(() => {
    setTitleProps("Clients");
  }, []);

  useEffect(() => {
    loadClients();
  }, [page, pageSize, sortModel, filters.searchQuery]);

  const onHandlePageSizeChange = (newSize) => {
    setPageSize(newSize);
  };

  const handleSortModelChange = (newModel) => {
    setSortModel(newModel);
  };

  const handleOnSearchQueryFilterChange = (searchQuery) => {
    setFilters({
      ...filters,
      searchQuery
    });
  };
  
  const handleRowSelection = useCallback((e) => {       
    setSelectedItem(e[0]);
  }, []);

  const handleUnselectItem = useCallback(() => {
    setSelectedItem(null);
  }, []);

  const handleDeleteConfirmation = useCallback((itemId) => {
    setDeleteItemId(itemId);
    setConfirmOpen(true);
  }, []);

  const handleDelete = (itemId) => {
    setLoading(true);
    apiRequest(`client/${itemId}`, {
      method: "DELETE"
    })
    .then(() => {
      enqueueSnackbar('Item successfully deleted!', {
        variant: 'success',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        autoHideDuration: 3000
      });
      loadClients();
      handleUnselectItem(); // Cerrar el panel de detalles después de eliminar
      // Invalidar caché de clientes para refrescar las listas
      invalidateCache('client');
    })
    .catch((error) => {
      setLoading(false);
      enqueueSnackbar(error.response?.data?.message ?? "An unknown error occurred", {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        autoHideDuration: 3000
      });
    });
  };

  const handleClientAdd = useCallback(() => {
    enqueueSnackbar('Item successfully added!', {
      variant: 'success',
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right',
      },
      autoHideDuration: 3000
    });
    setShowForm(false);
    loadClients();
  }, [enqueueSnackbar]);

  const handleEditItem = useCallback(() => {
    setShowForm(true);
  }, []);

  const handleDeleteItem = useCallback(() => {
    handleDeleteConfirmation(selectedItem);
  }, [selectedItem, handleDeleteConfirmation]);

  const columns = useMemo(() => [
    { 
      field: 'name', 
      headerName: 'Name', 
      minWidth: 300,
      sortable: true
    },
    { 
      field: 'identityNumber', 
      headerName: 'Identity Number', 
      minWidth: 200,
      sortable: true
    },
    {
      field: 'created',
      headerName: 'Created',
      minWidth: 150,
      type: 'dateTime',
      valueGetter: (params) => {
        return formatDate(params.value);
      },
      sortable: true
    }
  ], []);

  const memoizedClientPanel = useMemo(() => {
    if (!selectedItem && !showForm) return null;
    
    return (
      <ClientPanel
        selectedItem={selectedItem}
        showForm={showForm}
        handleUnselectItem={handleUnselectItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        handleDeleteConfirmation={handleDeleteConfirmation}
        handleClientAdd={handleClientAdd}
        setShowForm={setShowForm}
      />
    );
  }, [selectedItem, showForm, handleUnselectItem, handleEditItem, handleDeleteItem, handleDeleteConfirmation, handleClientAdd, setShowForm]);

  return (
    <div className="p-6">
      {/* Header with Add button and Search */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (selectedItem) {
                  setSelectedItem(null);
                }
                setShowForm(true);
              }}
              className="btn-primary flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add
            </button>
          </div>
          
          <div className="max-w-md">
            <SearchBar onTextChange={handleOnSearchQueryFilterChange} disabled={loading} />
          </div>
        </div>
      </div>

      {/* Table */}
      <CustomTable
        rows={clients.items}
        columns={columns}
        loading={loading}
        pageSize={pageSize}
        page={page}
        totalCount={clients.totalCount}
        onPageChange={setPage}
        onPageSizeChange={onHandlePageSizeChange}
        onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
        onRowClick={handleRowSelection}
        selectedRow={selectedItem}
        onView={(row) => setSelectedItem(row.id)}
        onEdit={(row) => {
          setSelectedItem(row.id);
          setShowForm(true);
        }}
        onDelete={(row) => handleDeleteConfirmation(row.id)}
      />

      {/* Detail Panel */}
      {(selectedItem || showForm) && (
        <div className="fixed inset-0 z-50 sm:inset-auto sm:right-0 sm:top-0 sm:h-full sm:w-96">
          {/* Mobile: Full screen overlay */}
          <div className="sm:hidden fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white shadow-xl w-full h-full flex flex-col">
              <div className="p-6 pb-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {showForm ? (selectedItem ? 'Edit Client' : 'Add Client') : 'Client Details'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      handleUnselectItem();
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    title="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                {memoizedClientPanel}
              </div>
            </div>
          </div>
          
          {/* Desktop: Drawer lateral derecho */}
          <div className="hidden sm:block fixed right-0 top-0 h-full w-96 bg-white shadow-xl">
            <div className="flex flex-col h-full">
              <div className="p-6 pb-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {showForm ? (selectedItem ? 'Edit Client' : 'Add Client') : 'Client Details'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      handleUnselectItem();
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    title="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {memoizedClientPanel}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog 
        title="Delete Client?"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => handleDelete(deleteItemId)}
      >
        Are you sure you want to delete this client?
      </DeleteConfirmDialog>
    </div>
  );
};

export default Clients;